<!-- Базовое поле ввода для форм и фильтров. -->
<template>
  <div class="input-wrapper">
    <!-- Обычные инпуты -->
    <input
      v-if="!isDateType && type !== 'big_number'"
      ref="input"
      :id="id"
      class="input"
      :class="{
        invalid: invalid,
        'has-password-toggle': isPassword && showPasswordToggle,
        'has-edit-btn': isEditable,
        'is-auto': isEditable && !isEditMode,
        'is-disabled': disabled,
      }"
      :type="currentInputType"
      :value="isEditable && !isEditMode ? '' : modelValue"
      :disabled="disabled || (isEditable && !isEditMode)"
      :autocomplete="autocomplete"
      :placeholder="
        isEditable && !isEditMode ? editablePlaceholder : placeholder
      "
      @blur="handleEditableBlur"
      @focus="$emit('focus', $event)"
      @input="handleInput"
      @keydown="$emit('keydown', $event)"
      @paste="$emit('paste', $event)"
    />

    <!-- Кнопка редактирования для editable-режима -->
    <button
      v-if="isEditable"
      class="edit-btn"
      type="button"
      tabindex="-1"
      :title="isEditMode ? 'Сбросить' : 'Редактировать'"
      @click.stop="toggleEditMode"
    >
      <!-- Карандаш — режим просмотра -->
      <svg
        v-if="!isEditMode"
        xmlns="http://www.w3.org/2000/svg"
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
        />
        <path
          d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
        />
      </svg>
      <!-- Крестик — режим редактирования (сбросить) -->
      <svg
        v-else
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="currentColor"
      >
        <path
          d="M1 1l12 12M13 1L1 13"
          stroke="currentColor"
          stroke-width="1.5"
        />
      </svg>
    </button>

    <!-- Ошибка пустого значения в editable-режиме -->
    <span v-if="editableEmptyError" class="editable-error">
      Поле не может быть пустым
    </span>

    <!-- Крестик для обычного инпута -->
    <button
      v-if="
        !isDateType &&
        type !== 'big_number' &&
        !isEditable &&
        modelValue &&
        clearable
      "
      class="clear-button"
      :class="{
        'clear-button--with-toggle': isPassword && showPasswordToggle,
      }"
      @click.stop="clearSelection"
      type="button"
      tabindex="-1"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="currentColor"
      >
        <path
          d="M1 1l12 12M13 1L1 13"
          stroke="currentColor"
          stroke-width="1"
        />
      </svg>
    </button>

    <!-- Кнопка показать/скрыть пароль - только если showPasswordToggle -->
    <button
      v-if="
        isPassword &&
        showPasswordToggle &&
        !isDateType &&
        type !== 'big_number'
      "
      class="password-toggle-btn"
      type="button"
      tabindex="-1"
      :aria-label="
        passwordVisible ? 'Скрыть пароль' : 'Показать пароль'
      "
      @click.stop="passwordVisible = !passwordVisible"
    >
      <!-- Глаз открытый - пароль скрыт -->
      <svg
        v-if="!passwordVisible"
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>

      <!-- Глаз перечёркнутый - пароль виден -->
      <svg
        v-else
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8
             a18.45 18.45 0 0 1 5.06-5.94"
        />
        <path
          d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8
             a18.5 18.5 0 0 1-2.16 3.19"
        />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    </button>

    <!-- Input для больших чисел -->
    <div v-else-if="type === 'big_number'" class="big-number-wrapper">
      <input
        ref="bigNumberInput"
        :id="id"
        class="input"
        :class="{ invalid: invalid }"
        type="text"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        @focus="$emit('focus', $event)"
        @keydown="$emit('keydown', $event)"
        @paste="$emit('paste', $event)"
      />
      <button
        v-if="modelValue != null && modelValue !== '' && clearable"
        class="clear-button"
        @click.stop="clearBigNumber"
        type="button"
        tabindex="-1"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="currentColor"
        >
          <path
            d="M1 1l12 12M13 1L1 13"
            stroke="currentColor"
            stroke-width="1"
          />
        </svg>
      </button>
    </div>

    <!-- Дата / Дата+Время -->
    <DatePicker
      v-else-if="isDateType"
      ref="datePicker"
      :model-value="parsedPickerValue"
      @update:model-value="handlePickerUpdate"
      :enable-time-picker="isDateTimeType"
      :is24="true"
      :auto-apply="datePickerAutoApply"
      :time-picker-inline="isDateTimeType"
      :text-input="true"
      :hide-input-icon="true"
      :clearable="false"
      :teleport="true"
      :locale="'ru'"
      :week-start="1"
      :month-name-format="'long'"
      :format="pickerFormat"
      :select-text="'Выбрать'"
      :cancel-text="'Отмена'"
      :now-button-label="'Сейчас'"
      :enable-seconds="false"
      @date-update="handlePickerDateUpdate"
      @internal-model-change="handlePickerInternalUpdate"
      @closed="onPickerClosed"
      @open="onPickerOpen"
    >
      <!-- Кастомный инпут через слот dp-input -->
      <template
        #dp-input="{
          onEnter,
          onTab,
          onBlur,
          onKeypress,
          onPaste,
          isMenuOpen,
        }"
      >
        <div
          class="date-trigger"
          :class="{ invalid: invalid, 'is-open': isMenuOpen }"
        >
          <input
            ref="dateInput"
            :id="id"
            class="date-input"
            type="text"
            :placeholder="
              isDateTimeType ? 'дд.мм.гггг, чч:мм' : 'дд.мм.гггг'
            "
            autocomplete="off"
            @focus="onDateInputFocus"
            @keypress="onKeypress"
            @keydown.enter="onEnter"
            @keydown.tab="onTab"
            @blur="onPickerInputBlur(onBlur)"
            @paste="handlePaste($event, onPaste)"
          />

          <div class="date-controls">
            <button
              v-if="modelValue && clearable"
              class="clear-button"
              @click.stop="clearDate"
              type="button"
              tabindex="-1"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="currentColor"
              >
                <path
                  d="M1 1l12 12M13 1L1 13"
                  stroke="currentColor"
                  stroke-width="1"
                />
              </svg>
            </button>
            <span class="calendar-icon">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <rect
                  x="1"
                  y="2"
                  width="14"
                  height="13"
                  rx="2"
                  stroke="currentColor"
                  stroke-width="1.2"
                />
                <path
                  d="M1 6h14"
                  stroke="currentColor"
                  stroke-width="1.2"
                />
                <path
                  d="M5 1v2M11 1v2"
                  stroke="currentColor"
                  stroke-width="1.2"
                  stroke-linecap="round"
                />
                <rect
                  x="4"
                  y="8.5"
                  width="2"
                  height="2"
                  rx="0.4"
                  fill="currentColor"
                />
                <rect
                  x="7"
                  y="8.5"
                  width="2"
                  height="2"
                  rx="0.4"
                  fill="currentColor"
                />
                <rect
                  x="10"
                  y="8.5"
                  width="2"
                  height="2"
                  rx="0.4"
                  fill="currentColor"
                />
                <rect
                  x="4"
                  y="11.5"
                  width="2"
                  height="2"
                  rx="0.4"
                  fill="currentColor"
                />
                <rect
                  x="7"
                  y="11.5"
                  width="2"
                  height="2"
                  rx="0.4"
                  fill="currentColor"
                />
              </svg>
            </span>
          </div>
        </div>
      </template>
    </DatePicker>
  </div>
</template>

<script>
import DatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import IMask from "imask";
import {
  buildDateInputEmitString,
  dateInputAutoApply,
  formatDateInputMaskValue,
  parseDateInputString,
  pickerDateUpdatePayload,
  shouldCommitDatePickerInternalUpdate,
} from "@/utils/baseInputDateUtils";

export default {
  name: "BaseInput",

  components: { DatePicker },

  props: {
    id: {
      type: String,
      default: null,
    },
    modelValue: [String, Number, Date],
    type: {
      type: String,
      default: "text",
    },
    placeholder: {
      type: String,
      default: "",
    },
    autocomplete: {
      type: String,
      default: "off",
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    timezone: {
      type: String,
      default: "",
    },
    isPassword: {
      type: Boolean,
      default: false,
    },
    showPasswordToggle: {
      type: Boolean,
      default: true,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    editablePlaceholder: {
      type: String,
      default: "Будет присвоен автоматически",
    },
  },

  emits: [
    "blur",
    "clear",
    "focus",
    "keydown",
    "paste",
    "update:modelValue",
  ],

  data() {
    return {
      mask: null,
      dateMask: null,
      updatingFromPicker: false,
      datePickerDateSelectionPending: false,
      passwordVisible: false,
      // true когда пользователь нажал карандаш и хочет ввести вручную
      isEditMode: false,
      editableEmptyError: false,
    };
  },

  computed: {
    isDateType() {
      return ["date", "datetime-local"].includes(this.type);
    },
    isDateTimeType() {
      return this.type === "datetime-local";
    },
    pickerFormat() {
      return this.isDateTimeType ? "dd.MM.yyyy, HH:mm" : "dd.MM.yyyy";
    },
    datePickerAutoApply() {
      return dateInputAutoApply(this.isDateTimeType);
    },

    currentInputType() {
      if (this.isPassword) {
        // Если toggle скрыт, то всегда тип password (звёздочки)
        if (!this.showPasswordToggle) return "password";
        return this.passwordVisible ? "text" : "password";
      }
      return this.type;
    },

    // Если передан editable и modelValue уже заполнен — сразу открываем режим редактирования
    isEditable() {
      return this.editable;
    },

    parsedPickerValue() {
      if (!this.modelValue) return null;
      if (this.modelValue instanceof Date) return this.modelValue;
      const str = String(this.modelValue);

      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const [y, m, d] = str.split("-").map(Number);
        return new Date(y, m - 1, d);
      }
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str)) {
        const [datePart, timePart] = str.split("T");
        const [y, mo, d] = datePart.split("-").map(Number);
        const [h, min] = timePart.split(":").map(Number);
        return new Date(y, mo - 1, d, h, min);
      }
      return null;
    },
  },

  watch: {
    // Если снаружи пришло заполненное значение (возврат со шага 2) — переключаемся в режим редактирования
    modelValue: {
      handler(newVal) {
        if (this.isEditable && newVal) {
          this.isEditMode = true;
        }

        if (this.type === "big_number") {
          this.updateMaskValue();
        }
        if (this.isDateType && !this.updatingFromPicker) {
          if (!newVal) {
            if (this.dateMask) this.dateMask.value = "";
            return;
          }
          const parsed = this.tryParseAnyDateString(String(newVal));
          if (parsed) this.setMaskValue(parsed);
        }
      },
    },
  },

  methods: {
    // Переводит фокус на активный нативный input внутри любого режима BaseInput
    focus() {
      const input =
        this.$refs.input ||
        this.$refs.bigNumberInput ||
        this.$el.querySelector("input");

      input?.focus();
    },

    toggleEditMode() {
      if (this.isEditMode) {
        // Сброс — очищаем значение и возвращаемся в авто-режим
        this.isEditMode = false;
        this.editableEmptyError = false;
        this.$emit("update:modelValue", "");
      } else {
        this.isEditMode = true;
        this.editableEmptyError = false;
      }
    },

    handleInput(event) {
      const val = event.target.value;

      if (this.isEditable) {
        this.editableEmptyError = false;
        // Если стёрли всё — показываем ошибку при blur, пока просто сохраняем пустую строку
        this.$emit("update:modelValue", val);
        return;
      }

      this.$emit("update:modelValue", val);
    },

    // Вызывается при blur в editable-режиме — проверяем не пустое ли
    handleEditableBlur(event) {
      if (this.isEditable && this.isEditMode) {
        const val = event.target.value;
        if (!val.trim()) {
          this.editableEmptyError = true;
        } else {
          this.editableEmptyError = false;
        }
      }

      this.$emit("blur", event);
    },

    clearSelection() {
      this.$emit("update:modelValue", null);
      this.$emit("clear");
    },

    clearBigNumber() {
      if (this.mask) {
        this.mask.unmaskedValue = "";
      }
      this.$emit("update:modelValue", null);
      this.$emit("clear");
    },

    updateMaskValue() {
      if (!this.mask) return;

      if (this.modelValue == null || this.modelValue === "") {
        this.mask.unmaskedValue = "";
        return;
      }

      this.mask.unmaskedValue = Number(this.modelValue).toString();
    },

    handlePickerUpdate(value) {
      this.updatingFromPicker = true;
      this.datePickerDateSelectionPending = false;
      if (!value) {
        this.$emit("update:modelValue", null);
        if (this.dateMask) this.dateMask.value = "";
        this.updatingFromPicker = false;
        return;
      }

      const payload = pickerDateUpdatePayload(
        value,
        this.isDateTimeType
      );
      if (!payload) {
        this.updatingFromPicker = false;
        return;
      }

      // DatePicker может закрыться сразу после выбора времени.
      // Маску обновляем до emit, иначе @closed/@blur успевает закоммитить
      // старое значение маски и визуально откатывает выбранное время.
      if (this.dateMask) this.dateMask.value = payload.maskValue;
      this.$emit("update:modelValue", payload.emitted);

      this.$nextTick(() => {
        this.updatingFromPicker = false;
      });
    },

    // Контракт datetime-local:
    // - открытие popup само по себе не меняет modelValue;
    // - клик по дате не коммитит значение, чтобы пользователь успел выбрать время;
    // - изменение времени считается осознанным выбором даты-времени и сразу
    //   синхронизирует modelValue с видимой маской;
    // - кнопка "Выбрать" работает как явное подтверждение текущего значения.
    handlePickerDateUpdate() {
      if (!this.isDateTimeType) return;

      // В datetime-local клик по дате только выбирает день.
      // Значение сохраняем после выбора времени или явного "Выбрать",
      // иначе пользователь не успевает заполнить часы и минуты.
      this.datePickerDateSelectionPending = true;
    },

    handlePickerInternalUpdate(value) {
      const shouldCommit = shouldCommitDatePickerInternalUpdate(
        this.isDateTimeType,
        this.datePickerDateSelectionPending
      );

      this.datePickerDateSelectionPending = false;
      if (!shouldCommit) return;

      this.handlePickerUpdate(value);
    },

    buildEmitString(d) {
      return buildDateInputEmitString(d, this.isDateTimeType);
    },

    clearDate() {
      if (this.dateMask) this.dateMask.value = "";
      this.$emit("update:modelValue", null);
      this.$emit("clear");
    },

    onPickerOpen() {
      this.datePickerDateSelectionPending = false;
    },

    onPickerClosed() {
      this.datePickerDateSelectionPending = false;
      this.commitMaskValue();
    },

    onPickerInputBlur(dpBlur) {
      this.commitMaskValue();
      dpBlur();
    },

    onDateInputFocus() {
      this.$nextTick(() => {
        if (this.$refs.datePicker?.openMenu) {
          this.$refs.datePicker.openMenu();
        }
      });
    },

    handlePaste(event, dpOnPaste) {
      event.preventDefault();
      const text = (event.clipboardData || window.clipboardData)
        .getData("text")
        .trim();

      const parsed = this.tryParseAnyDateString(text);
      if (parsed && !isNaN(parsed.getTime())) {
        this.setMaskValue(parsed);
        this.$nextTick(() => {
          this.commitMaskValue();
        });
      } else {
        dpOnPaste(event);
      }
    },

    tryParseAnyDateString(str) {
      return parseDateInputString(str);
    },

    commitMaskValue() {
      if (!this.dateMask) return;
      const val = this.dateMask.value;
      if (!val) return;

      const parsed = this.tryParseAnyDateString(val);
      if (parsed && !isNaN(parsed.getTime())) {
        const emitted = this.buildEmitString(parsed);
        if (emitted !== this.modelValue) {
          this.$emit("update:modelValue", emitted);
        }
      }
    },

    setMaskValue(date) {
      if (!this.dateMask || !date) return;
      this.dateMask.value = formatDateInputMaskValue(
        date,
        this.isDateTimeType
      );
    },

    pad(n) {
      return String(n).padStart(2, "0");
    },

    initDateMask() {
      if (!this.$refs.dateInput) return;

      this.dateMask = IMask(this.$refs.dateInput, {
        mask: Date,
        pattern: this.isDateTimeType ? "d.`m.`Y`, `HH:MM" : "d.`m.`Y",
        blocks: {
          d: {
            mask: IMask.MaskedRange,
            from: 1,
            to: 31,
            maxLength: 2,
          },
          m: {
            mask: IMask.MaskedRange,
            from: 1,
            to: 12,
            maxLength: 2,
          },
          Y: { mask: IMask.MaskedRange, from: 1900, to: 2100 },
          HH: {
            mask: IMask.MaskedRange,
            from: 0,
            to: 23,
            maxLength: 2,
          },
          MM: {
            mask: IMask.MaskedRange,
            from: 0,
            to: 59,
            maxLength: 2,
          },
        },
        format: (date) => {
          const p = this.pad;
          if (this.isDateTimeType) {
            return `${p(date.getDate())}.${p(
              date.getMonth() + 1
            )}.${date.getFullYear()}, ${p(date.getHours())}:${p(
              date.getMinutes()
            )}`;
          }
          return `${p(date.getDate())}.${p(
            date.getMonth() + 1
          )}.${date.getFullYear()}`;
        },
        parse: (str) =>
          this.tryParseAnyDateString(str) || new Date(NaN),
        lazy: true,
        autofix: true,
      });

      this.dateMask.on("complete", () => {
        this.commitMaskValue();
      });

      if (this.modelValue) {
        const parsed = this.tryParseAnyDateString(
          String(this.modelValue)
        );
        if (parsed) this.setMaskValue(parsed);
      }
    },
  },

  mounted() {
    // Если editable и значение уже есть при маунте — сразу в режим редактирования
    if (this.isEditable && this.modelValue) {
      this.isEditMode = true;
    }

    if (this.type === "big_number") {
      this.mask = IMask(this.$refs.bigNumberInput, {
        mask: Number,
        thousandsSeparator: " ",
        radix: ".",
        mapToRadix: [".", ","],
        scale: 2,
        signed: false,
      });

      if (this.modelValue != null && this.modelValue !== "") {
        this.updateMaskValue();
      }

      this.mask.on("accept", () => {
        const unmasked = this.mask.unmaskedValue;
        this.$emit(
          "update:modelValue",
          unmasked ? Number(unmasked) : null
        );
      });
    }

    if (this.isDateType) {
      this.$nextTick(() => {
        this.initDateMask();
      });
    }
  },

  beforeUnmount() {
    if (this.mask) this.mask.destroy();
    if (this.dateMask) this.dateMask.destroy();
  },
};
</script>

<style scoped>
.input-wrapper {
  position: relative;
}

.input {
  width: 100%;
  height: 32px;
  min-height: 32px;
  padding: 0 30px 0 10px;
  border: 1px solid #999;
  border-radius: 6px;
  font-size: 14px;
  line-height: 30px;
  font-family: inherit;
  box-sizing: border-box;
}

.input.has-password-toggle {
  padding-right: 62px;
}

/* Отступ под кнопку редактирования */
.input.has-edit-btn {
  padding-right: 34px;
}

/* Стиль disabled авто-режима */
.input.is-auto,
.input.is-disabled {
  background-color: #f5f5f5;
  color: #999;
  cursor: default;
  border-style: dashed;
}

.input.is-auto::placeholder,
.input.is-disabled::placeholder {
  opacity: 0.8;
  font-style: italic;
}

.input::placeholder {
  opacity: 0.5;
}

.input:focus {
  outline: 1px solid var(--color-green);
}

.input.invalid {
  border-color: var(--color-danger);
}

/* Кнопка редактирования (карандаш / крестик) */
.edit-btn {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: #aaa;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: color 0.2s ease;
}

.edit-btn:hover {
  color: var(--color-dark);
}

/* Сообщение об ошибке пустого поля */
.editable-error {
  display: block;
  margin-top: 5px;
  font-size: 13px;
  color: var(--color-danger);
}

.input-wrapper > .clear-button {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: #999;
  cursor: pointer;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.input-wrapper > .clear-button.clear-button--with-toggle {
  right: 32px;
}

.input-wrapper > .clear-button:hover {
  color: var(--color-danger);
}

.password-toggle-btn {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: #999;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.password-toggle-btn svg {
  width: 16px;
  height: 16px;
}

.password-toggle-btn:hover {
  color: var(--color-dark);
}

.big-number-wrapper {
  position: relative;
}

.big-number-wrapper .clear-button {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: #999;
  cursor: pointer;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.big-number-wrapper .clear-button:hover {
  color: var(--color-danger);
}

.date-trigger {
  display: flex;
  align-items: center;
  height: 32px;
  min-height: 32px;
  padding: 0 10px;
  border: 1px solid #999;
  border-radius: 6px;
  background: #fff;
  width: 100%;
  box-sizing: border-box;
  cursor: text;
  transition: border-color 0.2s ease;
}

.date-trigger:hover {
  border-color: var(--color-green);
}

.date-trigger.is-open {
  border-color: var(--color-green);
  outline: 1px solid var(--color-green);
}

.date-trigger.invalid {
  border-color: var(--color-danger);
}

.date-trigger.invalid.is-open {
  outline: 1px solid var(--color-danger);
}

.date-input {
  flex: 1;
  min-width: 0;
  height: 30px;
  min-height: 30px;
  border: none;
  outline: none;
  font-size: 14px;
  line-height: 30px;
  font-family: inherit;
  background: transparent;
  color: inherit;
  padding: 0;
  cursor: text;
}

.date-input::placeholder {
  opacity: 0.5;
}

.date-controls {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 6px;
  flex-shrink: 0;
}

.date-controls .clear-button {
  position: static;
  transform: none;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: #999;
  cursor: pointer;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.date-controls .clear-button:hover {
  color: var(--color-danger);
}

.calendar-icon {
  color: #999;
  display: flex;
  align-items: center;
  pointer-events: none;
  flex-shrink: 0;
}

.calendar-icon svg {
  width: 14px;
  height: 14px;
}

:deep(.dp__main) {
  width: 100%;
}

:deep(.dp__input_wrap) {
  width: 100%;
}

:deep(.dp__menu) {
  font-family: inherit;
  border-radius: 8px;
  border-color: #999;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

:deep(.dp__action_select) {
  background: var(--color-green);
}

:deep(.dp__action_select:hover) {
  background: var(--color-green-hover);
}

:deep(.dp__today) {
  border-color: var(--color-green);
}

:deep(.dp__date_hover:hover),
:deep(.dp__date_hover_start:hover),
:deep(.dp__date_hover_end:hover) {
  background: #d5ecea;
}

:deep(.dp__active_date),
:deep(.dp__range_start),
:deep(.dp__range_end) {
  background: var(--color-green);
}

:deep(.dp__active_date:hover) {
  background: var(--color-green-hover);
}

:deep(.dp__button:hover),
:deep(.dp__month_year_select:hover) {
  background: #d5ecea;
}

:deep(.dp__time_display:hover) {
  background: #d5ecea;
}

:deep(.dp__inc_dec_button:hover) {
  background: #d5ecea;
  color: var(--color-dark);
}

:deep(.dp__cancel) {
  color: var(--color-dark);
}

:deep(.dp__cancel:hover) {
  background: #f3f4f6;
}
</style>
